import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  IAuditEvent,
  IAuditEventData,
  IAuditEventDataDiff,
  IAuditEventType,
  IAuditReferenceType,
  ListAuditEventsRequest
} from '@interfaces/nebula';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { isHasProperty } from '@factories/helpers';

export const passwordChangesKeys = ['passwordHash', 'securityStamp'];
export const profileImageKey = 'profileImageId';
export const substitutesKey = 'substitutes';
export const SuccessLogonKeys = [
  'lastSuccessfulLogonTimestamp',
  'previousSuccessfulLogonTimestamp',
  'successfulLogonCount'
];
export const FailedLogonKeys = ['lastFailedLogonTimestamp', 'failedLogonCount'];
export const LastFailedLogonType = 'lastFailedLogonType';
export const FailedLogonCountKey = 'accessFailedCount';

export interface IAuditConfiguration {
  isEnabled: boolean;
}

export interface IAuditResponse {
  auditEvents: IAuditEvent[];
  countWithoutPaging: number;
  pageIndex: number;
  pagingSize: number;
}

@Injectable({
  providedIn: 'root'
})
export class AuditService {
  private http = inject(HttpClient);
  private baseUrl = inject<string>('BASE_URL' as never);

  referencesTypes: IAuditReferenceType[] = [];
  eventTypes: IAuditEventType[] = [];
  readonly #apiBaseUrl: string;

  constructor() {
    this.#apiBaseUrl = `${this.baseUrl}api/v1.2/audits`;
    this.getReferenceTypes().subscribe();
    this.getEventTypes().subscribe();
  }

  getConfiguration(): Observable<IAuditConfiguration> {
    return this.http.get<IAuditConfiguration>(`${this.#apiBaseUrl}/configuration`);
  }

  getReferenceTypes(): Observable<IAuditReferenceType[]> {
    if (this.referencesTypes?.length) {
      return of(this.referencesTypes);
    }
    return this.http.get<IAuditReferenceType[]>(`${this.#apiBaseUrl}/references/types`).pipe(
      tap(data => {
        this.referencesTypes = data;
      })
    );
  }

  getEventTypes(): Observable<IAuditEventType[]> {
    if (this.eventTypes?.length) {
      return of(this.eventTypes);
    }
    return this.http.get<IAuditEventType[]>(`${this.#apiBaseUrl}/events/types`).pipe(
      tap(data => {
        this.eventTypes = data;
      })
    );
  }

  getEvents(request: ListAuditEventsRequest): Observable<IAuditResponse> {
    let params = new HttpParams();

    if (request.eventTypes?.length) {
      request.eventTypes.forEach(type => {
        params = params.append('types', type);
      });
    }

    if (request.minCreationTimestamp) {
      let creationDate = request.minCreationTimestamp as string;
      if (request.minCreationTimestamp instanceof Date) {
        creationDate = request.minCreationTimestamp.toISOString();
      }
      params = params.set('min-creation-timestamp', creationDate);
    }
    if (request.maxCreationTimestamp) {
      let creationDate = request.maxCreationTimestamp as string;
      if (request.maxCreationTimestamp instanceof Date) {
        creationDate = request.maxCreationTimestamp.toISOString();
      }
      params = params.set('max-creation-timestamp', creationDate);
    }

    if (request.minModificationTimestamp) {
      params = params.set('min-update-timestamp', request.minModificationTimestamp.toISOString());
    }

    if (request.pagingSize !== undefined && request.pagingSize !== null) {
      params = params.set('paging-size', request.pagingSize.toString());
    }

    if (request.pageIndex !== undefined && request.pageIndex !== null) {
      params = params.set('page-idx', request.pageIndex.toString());
    }

    if (request.userIdFilterMode !== undefined && request.userIdFilterMode !== null) {
      params = params.set('user-fm', request.userIdFilterMode.toString());
    }

    if (request.userIdSearchParameter) {
      params = params.set('user', request.userIdSearchParameter);
    }

    if (request.impersonatedUserIdFilterMode !== undefined && request.impersonatedUserIdFilterMode !== null) {
      params = params.set('imp-user-fm', request.impersonatedUserIdFilterMode.toString());
    }

    if (request.impersonatedUserIdSearchParameter) {
      params = params.set('imp-user', request.impersonatedUserIdSearchParameter);
    }

    if (request.referenceTypeFilterMode !== undefined && request.referenceTypeFilterMode !== null) {
      params = params.set('ref-type-fm', request.referenceTypeFilterMode.toString());
    }

    if (request.referenceTypeSearchParameter !== undefined && request.referenceTypeSearchParameter !== null) {
      params = params.set('ref-type', request.referenceTypeSearchParameter.toString());
    }

    if (request.referenceIdentifierFilterMode !== undefined && request.referenceIdentifierFilterMode !== null) {
      params = params.set('ref-id-fm', request.referenceIdentifierFilterMode.toString());
    }

    if (request.referenceIdentifierSearchParameter) {
      params = params.set('ref-id', request.referenceIdentifierSearchParameter);
    }

    if (request.sortField !== undefined && request.sortField !== null) {
      params = params.set('sort-field', request.sortField.toString());
    }

    if (request.isSortAscending !== undefined && request.isSortAscending !== null) {
      params = params.set('sort-asc', request.isSortAscending);
    }

    if (isHasProperty(request, 'includesDiffs')) {
      params = params.set('incl-diffs', request.includesDiffs);
    }

    if (isHasProperty(request, 'includesData')) {
      params = params.set('incl-data', request.includesData);
    }

    if (isHasProperty(request, 'take')) {
      params = params.set('take', request.take);
    }

    return this.http.get<IAuditResponse>(`${this.#apiBaseUrl}`, { params }).pipe(
      map(response => {
        response.auditEvents.map(audit => {
          return this.mapEventData(audit);
        });
        return response;
      })
    );
  }

  getEventsCurrentUser(request: ListAuditEventsRequest): Observable<IAuditResponse> {
    let params = new HttpParams();

    if (request.eventTypes?.length) {
      request.eventTypes.forEach(type => {
        params = params.append('types', type);
      });
    }

    if (request.minCreationTimestamp) {
      let creationDate = request.minCreationTimestamp as string;
      if (request.minCreationTimestamp instanceof Date) {
        creationDate = request.minCreationTimestamp.toISOString();
      }
      params = params.set('min-creation-timestamp', creationDate);
    }
    if (request.maxCreationTimestamp) {
      let creationDate = request.maxCreationTimestamp as string;
      if (request.maxCreationTimestamp instanceof Date) {
        creationDate = request.maxCreationTimestamp.toISOString();
      }
      params = params.set('max-creation-timestamp', creationDate);
    }

    if (request.minModificationTimestamp) {
      params = params.set('min-update-timestamp', request.minModificationTimestamp.toISOString());
    }

    if (request.pagingSize !== undefined && request.pagingSize !== null) {
      params = params.set('paging-size', request.pagingSize.toString());
    }

    if (request.pageIndex !== undefined && request.pageIndex !== null) {
      params = params.set('page-idx', request.pageIndex.toString());
    }

    if (request.userIdFilterMode !== undefined && request.userIdFilterMode !== null) {
      params = params.set('user-fm', request.userIdFilterMode.toString());
    }

    if (request.userIdSearchParameter) {
      params = params.set('user', request.userIdSearchParameter);
    }

    if (request.impersonatedUserIdFilterMode !== undefined && request.impersonatedUserIdFilterMode !== null) {
      params = params.set('imp-user-fm', request.impersonatedUserIdFilterMode.toString());
    }

    if (request.impersonatedUserIdSearchParameter) {
      params = params.set('imp-user', request.impersonatedUserIdSearchParameter);
    }

    if (request.sortField !== undefined && request.sortField !== null) {
      params = params.set('sort-field', request.sortField.toString());
    }

    if (request.isSortAscending !== undefined && request.isSortAscending !== null) {
      params = params.set('sort-asc', request.isSortAscending);
    }
    if (isHasProperty(request, 'includesDiffs')) {
      params = params.set('incl-diffs', request.includesDiffs);
    }
    if (isHasProperty(request, 'includesData')) {
      params = params.set('incl-data', request.includesData);
    }

    return this.http.get<IAuditResponse>(`${this.#apiBaseUrl}/by-reference-type/user/current`, { params }).pipe(
      map(response => {
        response.auditEvents.map(audit => {
          return this.mapEventData(audit);
        });
        return response;
      })
    );
  }

  getAuditEventCurrentUser(id: number): Observable<IAuditEvent> {
    return this.http.get<IAuditEvent>(`${this.#apiBaseUrl}/by-reference-type/user/current/${id}`).pipe(
      map(response => {
        return this.mapEventData(response);
      })
    );
  }

  getAuditEvent(id: number): Observable<IAuditEvent> {
    return this.http.get<IAuditEvent>(`${this.#apiBaseUrl}/${id}`).pipe(
      map(response => {
        return this.mapEventData(response);
      })
    );
  }

  mapEventData(audit: IAuditEvent): IAuditEvent {
    try {
      if (audit.data) {
        audit.data = this.convertKeysToLowercase(
          JSON.parse(audit.data as unknown as string) as Record<string, unknown>
        ) as IAuditEventData;
      }
    } catch {
      // nothing
    }
    try {
      if (Array.isArray(audit.diffData)) {
        audit.diffData = this.convertKeysToLowercase(
          JSON.parse(audit.diffData[1] as unknown as string) as Record<string, unknown>
        ) as IAuditEventDataDiff;
      } else {
        audit.diffData = this.convertKeysToLowercase(
          JSON.parse(audit.diffData as unknown as string) as Record<string, unknown>
        ) as IAuditEventDataDiff;
      }
    } catch {
      // nothing
    }
    audit.eventTypeName = this.eventTypes?.find(e => e.value === audit.eventType)?.displayName || audit.eventType;
    audit.isInclude = true;
    if (audit.eventType === 'User:Update') {
      if (
        audit.diffData &&
        Object.keys(audit.diffData).every(key => FailedLogonKeys.includes(key) || key === LastFailedLogonType)
      ) {
        audit.eventTypeName = 'Fehlgeschlagene Anmeldung';
      }
      if (audit.diffData && Object.keys(audit.diffData).every(key => key === FailedLogonCountKey)) {
        audit.eventTypeName = 'Anzahl fehlgeschlagener Anmeldungen';
      }
      if (audit.diffData && Object.keys(audit.diffData).every(key => SuccessLogonKeys.includes(key))) {
        audit.eventTypeName = 'Erfolgreiche Anmeldung';
      }
      if (audit.diffData && Object.keys(audit.diffData).every(key => key === substitutesKey)) {
        audit.eventTypeName = 'Vertrerter geändert';
      }
      if (audit.diffData && Object.keys(audit.diffData).every(key => key === profileImageKey)) {
        audit.eventTypeName = 'Profilbild geändert';
      }
      if (audit.diffData && Object.keys(audit.diffData).every(key => passwordChangesKeys.includes(key))) {
        audit.eventTypeName = 'Passwort geändert';
      }
    }
    if (audit.eventType === 'User:Logout') {
      if (audit.data?.customFields?.isAutomaticLogout) {
        audit.eventTypeName = 'Benutzer automatisch abgemeldet';
      } else {
        audit.isInclude = false;
      }
    }
    audit.referenceTypeName =
      this.referencesTypes?.find(e => e.serverValue === audit.referenceType)?.description || audit.referenceTypeName;
    return audit;
  }

  convertKeysToLowercase(obj: Record<string, unknown>): unknown {
    if (Array.isArray(obj)) {
      return obj.map(i => this.convertKeysToLowercase(i as Record<string, unknown>));
    } else if (obj !== null && typeof obj === 'object') {
      return Object.keys(obj).reduce(
        (acc, key) => {
          const newKey = /^[A-Z]/.test(key) ? key.charAt(0).toLowerCase() + key.slice(1) : key;

          acc[newKey] = this.convertKeysToLowercase(obj[key] as Record<string, unknown>);
          return acc;
        },
        {} as Record<string, unknown>
      );
    }
    return obj;
  }
}
